import { route } from 'preact-router';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Button } from '../../components/ui/Button.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { api } from '../../services/api.js';
import { fileFrom, refusalWording, uploadPhoto } from './InspectionCapturePage.js';

/**
 * 415 (#1520): the fuel photo. Before the key is out of the lockbox the car cannot be started, so the dashboard is
 * asked here, after the code: one photo with the engine on, sent once, inside the pickup window. The platform decides
 * whether it may be sent; this screen says what it was told. Nothing waits on it.
 */
export function FuelPhotoPage({ id }: { id?: string; path?: string }) {
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  async function submit() {
    if (!file || sending) return;
    setError(null);
    setSending(true);
    try {
      const photoId = await uploadPhoto(file);
      await api.post(`/bookings/${id}/inspections/fuel`, { photo_id: photoId });
      route(`/booking/${id}/confirm`);
    } catch (err) {
      setError(refusalWording(err));
      setSending(false);
    }
  }

  return (
    <div className="bg-bg px-4 py-6">
      <PageHeader title="Fuel photo" fallback={`/booking/${id}/confirm`} />
      <p className="mb-3 text-sm text-text-muted">One photo of the dashboard with the engine on.</p>
      <label
        data-testid="fuel-shot"
        className={`relative flex aspect-[4/3] cursor-pointer flex-col items-center justify-center gap-1 overflow-hidden rounded-xl text-xs text-text-muted ${
          preview ? 'bg-surface-mid' : 'border-[1.5px] border-dashed border-border bg-surface-mid'
        }`}
      >
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="sr-only"
          aria-label="Dashboard photo"
          onChange={(e) => {
            const f = fileFrom(e);
            if (f) setFile(f);
          }}
        />
        {preview ? (
          <img
            src={preview}
            alt="Dashboard"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <>
            <span className="text-2xl" aria-hidden="true">
              📷
            </span>
            <span className="font-bold text-text">Dashboard</span>
            <span>fuel gauge visible, engine on</span>
          </>
        )}
      </label>
      {error && (
        <p role="alert" data-testid="fuel-error" className="mt-3 text-sm text-error">
          {error}
        </p>
      )}
      <Button
        data-testid="fuel-submit"
        disabled={!file}
        loading={sending}
        onClick={() => void submit()}
        size="lg"
        className="mt-3 w-full"
      >
        Send fuel photo
      </Button>
      <p className="mt-2 text-center text-xs text-text-muted">You can send this once.</p>
    </div>
  );
}
